import Ajv from "ajv";
import ajvErrors from "ajv-errors";
import addFormats from "ajv-formats";

import {
	decodeFileValue,
	fileSize,
	isBase64
} from "./files";
import {
	pointerSegment,
	resolveSchema
} from "./schema";
import type {
	FormMetadata,
	JsonSchemaDefinition,
	ValidationError
} from "./types";
import {
	isRecord,
	isValueArray
} from "./values";
import { formatValidationErrors } from "./validationMessages";

export function createFormAjv(): Ajv {
	const ajv = new Ajv({
		allErrors: true,
		verbose: true,
		strict: false,
		validateFormats: true,
		coerceTypes: false,
		useDefaults: false,
		removeAdditional: false
	});

	addFormats(ajv);
	ajvErrors(ajv);

	ajv.addFormat("phone", {
		type: "string",
		validate: (value: string) => /^\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/.test(value)
	});
	ajv.addFormat("base64", {
		type: "string",
		validate: isBase64
	});
	ajv.addFormat("file", {
		type: "string",
		validate: (value: string) => decodeFileValue(value) !== undefined
	});
	ajv.addFormat("entity", {
		type: "number",
		validate: Number.isFinite
	});
	ajv.addFormat("datapoint", {
		type: "number",
		validate: Number.isFinite
	});
	ajv.addKeyword({
		keyword: "metadata",
		schemaType: "object",
		type: "string",
		errors: false,
		metaSchema: {
			type: "object",
			properties: {
				maxSize: {
					type: "integer",
					minimum: 0
				}
			}
		},
		error: { message: "must not exceed the configured file size limit" },
		compile(metadata: FormMetadata, schema: JsonSchemaDefinition) {
			const maximum = metadata.maxSize;
			const isFile = schema.format === "file" || schema.format === "base64";

			return (value: unknown): boolean => {
				const size = fileSize(value);

				return !isFile || maximum === undefined || size === undefined || size <= maximum;
			};
		}
	});

	return ajv;
}

const isValidationError = (value: unknown): value is ValidationError =>
	isRecord(value)
	&& typeof value.keyword === "string"
	&& typeof value.instancePath === "string"
	&& typeof value.schemaPath === "string"
	&& isRecord(value.params);

const nestedErrors = (error: ValidationError): ValidationError[] =>
	isValueArray(error.params.errors)
		? error.params.errors.filter(isValidationError)
		: [];

const pathPointer = (path: string): string =>
	path
		? `/${path.split(".")
			.map(pointerSegment)
			.join("/")}`
		: "";

export function errorPath(error: ValidationError): string {
	const nested = nestedErrors(error);

	if(error.keyword === "errorMessage" && nested.length === 1) {
		return errorPath(nested[0]);
	}

	const missing = error.keyword === "required" && typeof error.params.missingProperty === "string"
		? error.params.missingProperty
		: undefined;

	return `${error.instancePath}${missing ? `/${pointerSegment(missing)}` : ""}`;
}

export function errorsAtPath(
	errors: ValidationError[],
	path: string
): string[] {
	const pointer = pathPointer(path);
	const messages = errors
		.filter(error => errorPath(error) === pointer)
		.map(error => error.message ?? "Invalid value");

	return [...new Set(messages)];
}

/** Defer only the edited collection's completion constraints, retaining its root reference context. */
function collectionDraftSchema(
	root: JsonSchemaDefinition,
	node: JsonSchemaDefinition,
	segments: string[],
	data: unknown
): JsonSchemaDefinition {
	const resolved = resolveSchema(root, node);

	// JSON Forms' declaration type omits boolean schemas, but AJV supports them.
	// Preserve rejecting branches when relaxing collection completion constraints.
	if(typeof resolved === "boolean") {
		return resolved;
	}

	const copy = { ...resolved };

	if(segments.length === 0) {
		delete copy.minItems;
		delete copy.contains;
	}

	if(segments.length > 0 && Array.isArray(data)) {
		const [index, ...remaining] = segments;
		const itemData: unknown = data[Number(index)];

		if(Array.isArray(resolved.items)) {
			copy.items = resolved.items.map((item, itemIndex) =>
				itemIndex === Number(index)
					? collectionDraftSchema(root, item, remaining, itemData)
					: item);

			if(Number(index) >= resolved.items.length && typeof resolved.additionalItems === "object") {
				copy.additionalItems = collectionDraftSchema(root, resolved.additionalItems, remaining, itemData);
			}
		} else if(resolved.items !== undefined) {
			copy.items = collectionDraftSchema(root, resolved.items, remaining, itemData);
		}
	}

	if(segments.length > 0 && isRecord(data)) {
		const [property, ...remaining] = segments;
		const child = resolved.properties?.[property];

		if(child !== undefined) {
			copy.properties = {
				...resolved.properties,
				[property]: collectionDraftSchema(root, child, remaining, data[property])
			};
		}
	}

	for(const keyword of [
		"allOf",
		"anyOf",
		"oneOf"
	] as const) {
		const branches = copy[keyword];

		if(branches) {
			copy[keyword] = branches.map(branch => collectionDraftSchema(root, branch, segments, data));
		}
	}

	for(const keyword of [
		"then",
		"else"
	] as const) {
		const branch = copy[keyword];

		if(branch) {
			copy[keyword] = collectionDraftSchema(root, branch, segments, data);
		}
	}

	return copy;
}

function leafErrors(error: ValidationError): ValidationError[] {
	const nested = nestedErrors(error);

	if(error.keyword === "errorMessage" && nested.length > 0) {
		return nested.flatMap(leafErrors);
	}

	return [error];
}

/** Save a valid row while allowing an incomplete collection to be built progressively. */
export function collectionDraftErrors(
	errors: ValidationError[],
	arrayPath: string,
	itemPath: string,
	rootSchema?: JsonSchemaDefinition,
	data?: unknown,
	inputErrors: ValidationError[] = []
): string[] {
	if(rootSchema) {
		// Keep original definitions when item and contains constraints share references.
		const segments = arrayPath ? arrayPath.split(".") : [];
		const draftSchema = {
			...rootSchema,
			...collectionDraftSchema(rootSchema, rootSchema, segments, data)
		};

		delete draftSchema.$ref;

		const validate = createFormAjv()
			.compile(draftSchema);

		validate(data);
		errors = [...(validate.errors ?? []), ...inputErrors];
	}

	const arrayPointer = pathPointer(arrayPath);
	const itemPointer = pathPointer(itemPath);
	const containsPaths = errors.flatMap(leafErrors)
		.filter(error => error.instancePath === arrayPointer && error.keyword === "contains")
		.map(error => error.schemaPath);

	const relevant = (error: ValidationError): boolean => {
		const belongsToContains = containsPaths.some(path =>
			error.schemaPath === path || error.schemaPath.startsWith(`${path}/`));

		if(belongsToContains) {
			return false;
		}

		if(error.instancePath === arrayPointer && error.keyword === "minItems") {
			return false;
		}

		const path = errorPath(error);

		return path === arrayPointer || path === itemPointer || path.startsWith(`${itemPointer}/`);
	};

	const messages = formatValidationErrors(errors, rootSchema)
		.filter(error => leafErrors(error)
			.some(relevant))
		.map(error => error.message ?? "Invalid value");

	return [...new Set(messages)];
}
